import { useEffect, useId, useMemo, useState, type KeyboardEvent } from "react";
import type { Vehicle } from "@shared/modules/x-atelier";
import {
  findVehicleMake,
  searchVehicleMakes,
  searchVehicleModels,
  vehicleCorrection,
  vehicleModelYearMatch,
  type VehicleCatalog,
  type VehicleCatalogModel,
} from "@shared/modules/x-atelier/vehicules";
import { Button } from "../../../core/components/ui/button";
import { Field, Input } from "../../../core/components/ui/input";
import { useToastStore } from "../../../core/stores/toast";
import { updateVehicle } from "../lib/api";

// Saisie assistée de l'année, la marque et le modèle : listes recherchables,
// tolérantes aux fautes et aux noms écrits au son (« allander » → Highlander),
// modèles de l'année en premier. Le nom officiel choisi est celui qui s'écrit
// dans la fiche, le rendez-vous et les textos ; une saisie libre reste possible
// pour un véhicule absent du catalogue. Une ancienne fiche n'est jamais
// corrigée en silence : la correction est proposée, l'employé décide.

let catalogPromise: Promise<VehicleCatalog> | null = null;

/** Le catalogue (≈ 50 Ko) se charge à la première saisie d'un véhicule, puis reste en mémoire. */
export function loadVehicleCatalog(): Promise<VehicleCatalog> {
  catalogPromise ??= import("@shared/modules/x-atelier/vehicules-catalogue")
    .then((m) => m.VEHICLE_CATALOG)
    .catch((err) => {
      catalogPromise = null;
      throw err;
    });
  return catalogPromise;
}

export function useVehicleCatalog(): VehicleCatalog | null {
  const [catalog, setCatalog] = useState<VehicleCatalog | null>(null);
  useEffect(() => {
    let alive = true;
    loadVehicleCatalog()
      .then((c) => alive && setCatalog(c))
      .catch(() => undefined); // hors ligne : la saisie libre reste possible
    return () => {
      alive = false;
    };
  }, []);
  return catalog;
}

/** Marques proposées avant toute frappe : les plus courantes au Québec. */
const POPULAR_MAKES = [
  "Toyota",
  "Honda",
  "Hyundai",
  "Kia",
  "Ford",
  "Chevrolet",
  "Mazda",
  "Nissan",
  "Subaru",
  "Volkswagen",
  "GMC",
  "Ram",
  "Jeep",
  "Mitsubishi",
  "Tesla",
];

export interface ComboOption {
  value: string;
  meta?: string;
}

/**
 * Liste déroulante recherchable : flèches, Entrée et Échap au clavier, clic
 * ou toucher. La dernière option garde la saisie telle quelle (hors catalogue).
 */
export function VehicleCombobox({
  id,
  value,
  onChange,
  options,
  placeholder,
  required,
  autoFocus,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboOption[];
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const typed = value.trim();
  const exact = options.some((o) => o.value.toLowerCase() === typed.toLowerCase());
  const items = [
    ...options.map((o) => ({ ...o, free: false })),
    ...(typed && !exact ? [{ value: typed, meta: "hors catalogue", free: true }] : []),
  ];
  const shown = open && items.length > 0;

  const pick = (item: { value: string }) => {
    onChange(item.value);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter" && shown) {
      // Entrée choisit dans la liste ; elle ne soumet pas le formulaire.
      e.preventDefault();
      if (active >= 0 && items[active]) pick(items[active]!);
      else setOpen(false);
    } else if (e.key === "Escape" && shown) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className="combobox">
      <input
        id={id}
        className="input"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={listId}
        aria-activedescendant={shown && active >= 0 ? `${listId}-${active}` : undefined}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        autoComplete="off"
        autoCapitalize="words"
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={onKeyDown}
      />
      {shown && (
        <ul className="combobox__list" id={listId} role="listbox">
          {items.map((item, i) => (
            <li
              key={`${item.free ? "libre" : "cat"}-${item.value}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`combobox__option${i === active ? " is-active" : ""}${item.free ? " combobox__option--free" : ""}`}
              // mousedown : choisir avant que le champ ne perde le focus.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(item);
              }}
            >
              <span className="combobox__value">
                {item.free ? `Garder « ${item.value} »` : item.value}
              </span>
              {item.meta && <span className="combobox__meta">{item.meta}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function yearsLabel(model: VehicleCatalogModel): string | undefined {
  if (!model.from) return undefined;
  return model.to ? `${model.from}–${model.to}` : `depuis ${model.from}`;
}

const yearOf = (year: string) => (/^(19|20)\d\d$/.test(year.trim()) ? Number(year.trim()) : null);

export interface VehicleIdentity {
  year: string;
  make: string;
  model: string;
}

/**
 * Année, marque et modèle. Les modèles suivent la marque et, quand le
 * catalogue le permet, l'année ; une faute fait proposer le nom officiel.
 */
export function VehicleIdentityFields({
  value,
  onChange,
  autoFocus,
}: {
  value: VehicleIdentity;
  onChange: (patch: Partial<VehicleIdentity>) => void;
  autoFocus?: boolean;
}) {
  const catalog = useVehicleCatalog();
  const year = yearOf(value.year);
  const make = catalog ? findVehicleMake(catalog, value.make) : null;

  const makeOptions = useMemo<ComboOption[]>(() => {
    if (!catalog) return [];
    if (!value.make.trim())
      return POPULAR_MAKES.filter((name) => findVehicleMake(catalog, name)).map((name) => ({
        value: name,
      }));
    return searchVehicleMakes(catalog, value.make, 8).map((m) => ({ value: m.make.name }));
  }, [catalog, value.make]);

  const modelOptions = useMemo<ComboOption[]>(() => {
    if (!catalog || !make) return [];
    return searchVehicleModels(
      catalog,
      make.name,
      value.model,
      year,
      value.model.trim() ? 8 : 40,
    ).map((m) => ({
      value: m.model.name,
      meta: m.yearMatch === false && year ? `inconnu en ${year}` : yearsLabel(m.model),
    }));
  }, [catalog, make, value.model, year]);

  const correction =
    catalog && value.make.trim() && value.model.trim()
      ? vehicleCorrection(catalog, value.make, value.model, year)
      : null;
  const official = make?.models.find((m) => m.name === value.model.trim());
  const yearMismatch = official && year ? vehicleModelYearMatch(official, year) === false : false;

  const modelHint = !value.make.trim()
    ? "Choisissez d’abord la marque."
    : !catalog
      ? undefined
      : !make
        ? "Marque hors catalogue : saisie libre."
        : yearMismatch
          ? `${official!.name} n’est pas connu pour ${year} : vérifiez l’année.`
          : official
            ? undefined
            : value.model.trim() && !correction
              ? "Modèle hors catalogue : il sera enregistré tel quel."
              : undefined;

  return (
    <>
      <Field label="Année">
        <Input
          inputMode="numeric"
          value={value.year}
          onChange={(e) => onChange({ year: e.target.value })}
          placeholder="ex : 2023"
          maxLength={4}
          autoFocus={autoFocus}
        />
      </Field>
      <Field label="Marque">
        <VehicleCombobox
          value={value.make}
          onChange={(make) => onChange({ make })}
          options={makeOptions}
          placeholder="ex : Toyota"
          required
        />
      </Field>
      <Field label="Modèle" hint={modelHint}>
        <VehicleCombobox
          value={value.model}
          onChange={(model) => onChange({ model })}
          options={modelOptions}
          placeholder={make ? "ex : Highlander" : "ex : Seltos"}
          required
        />
      </Field>
      {correction && (
        <p className="vehicle-hint" role="status">
          <span>
            Vouliez-vous dire{" "}
            <strong>
              {correction.make} {correction.model}
            </strong>{" "}
            ?
          </span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onChange({ make: correction.make, model: correction.model })}
          >
            Utiliser le nom officiel
          </Button>
        </p>
      )}
    </>
  );
}

const DISMISSED = "atelier.vehicules.corrections-ignorees";

function dismissKey(v: Vehicle) {
  return `${v.id}:${v.make}:${v.model}`;
}

function readDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED) ?? "[]") as string[];
  } catch {
    return [];
  }
}

/**
 * Sur une fiche existante dont le nom diffère du catalogue (« Toyota
 * allander »), propose le nom officiel : rien n'est modifié sans un clic.
 * « Garder tel quel » ne le repropose plus pour cette écriture.
 */
export function VehicleCorrectionHint({
  vehicle,
  onFixed,
}: {
  vehicle: Vehicle;
  onFixed: (vehicle: Vehicle) => void;
}) {
  const catalog = useVehicleCatalog();
  const push = useToastStore((s) => s.push);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => readDismissed().includes(dismissKey(vehicle)));
  useEffect(() => setDismissed(readDismissed().includes(dismissKey(vehicle))), [vehicle]);
  const suggestion =
    catalog && !dismissed
      ? vehicleCorrection(catalog, vehicle.make, vehicle.model, vehicle.year)
      : null;
  if (!suggestion) return null;

  const apply = async () => {
    setBusy(true);
    try {
      const saved = await updateVehicle(vehicle.id, {
        make: suggestion.make,
        model: suggestion.model,
      });
      push("success", "Fiche corrigée", `${suggestion.make} ${suggestion.model}`);
      onFixed(saved);
    } catch {
      push("danger", "Correction impossible", "Réessayez dans un instant.");
    } finally {
      setBusy(false);
    }
  };
  const keep = () => {
    try {
      localStorage.setItem(
        DISMISSED,
        JSON.stringify([...readDismissed(), dismissKey(vehicle)].slice(-500)),
      );
    } catch {
      // stockage indisponible : la proposition reviendra simplement
    }
    setDismissed(true);
  };

  return (
    <div className="vehicle-correction" role="note">
      <p className="vehicle-correction__text">
        Écrit « {vehicle.make} {vehicle.model} ». Nom officiel :{" "}
        <strong>
          {suggestion.make} {suggestion.model}
        </strong>
      </p>
      <div className="vehicle-correction__actions">
        <Button type="button" size="sm" onClick={apply} disabled={busy}>
          {busy ? "Correction…" : "Corriger la fiche"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={keep} disabled={busy}>
          Garder tel quel
        </Button>
      </div>
    </div>
  );
}
